import { cloneDeep } from 'lodash';
import React, { useMemo } from 'react';

import { ErpPreview } from '@work4all/components/lib/components/entity-preview/erp-preview/ErpPreview';
import { useErpPreviewData } from '@work4all/components/lib/components/entity-preview/erp-preview/hooks/use-erp-preview-data';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { EntityPreviewDataProviderProps } from '../../../../../../components/data-tables/entity-table/types';
import { mergeActions } from '../../../../../../components/data-tables/entity-table/utils';
import {
  ConvertEntity,
  useCloneConvertErpActions,
} from '../../../../../../components/data-tables/offers-table/hooks/use-clone-convert-erp';
import { usePositionDoneWithData } from '../../../../../../hooks/use-position-done';
import { usePositionDoneMutation } from '../../../../../../hooks/use-position-done/use-position-done-mutation';
import { useLanguageManager } from '../../../../../../providers/LanguageManagerProvider';
import { PdfOverlayMaskProvider } from '../../../../../mask-overlays/mask-overlay/views/pdf-editor/PdfOverlayMaskProvider';
import { FilePreviewPanelProps } from '../FilePreviewPanelProps';

type ErpPreviewContainerProps = FilePreviewPanelProps & {
  isApprovalCenter?: boolean;
  entries?: { id?: string | number }[];
  previewData?: ReturnType<typeof useErpPreviewData>;
};

export const ErpPreviewContainer = (props: ErpPreviewContainerProps) => {
  const {
    entityIds,
    entityType,
    openInFullscreen = false,
    previewData,
    ...commonProps
  } = props;

  const { resources: languageResources } = useLanguageManager();

  return (
    <PdfOverlayMaskProvider>
      <ErpPreview
        {...commonProps}
        openInFullscreen={openInFullscreen}
        ids={entityIds as number[]}
        entity={entityType}
        previewData={previewData}
        languageResources={languageResources}
      />
    </PdfOverlayMaskProvider>
  );
};

export const ErpPreviewDataProvider = (
  props: EntityPreviewDataProviderProps & {
    children: (
      props: EntityPreviewDataProviderProps & {
        previewData?: any;
      }
    ) => React.ReactNode;
  }
) => {
  const { children, ...dataProps } = props;
  const { entries, entityType, ...others } = dataProps;

  // Extract ids from entries if needed
  const entityIds =
    (entries
      ?.map((entry) => entry.id)
      .filter((id) => id !== undefined) as number[]) || [];

  const previewData = useErpPreviewData({
    ids: entityIds,
    entity: entityType,
  });

  const { actions } = useCloneConvertErpActions({
    entity: entityType as ConvertEntity,
    data: previewData.data,
  });

  const onPositionDone = usePositionDoneMutation();

  const { actions: positionDoneActions } = usePositionDoneWithData({
    contractId: entityIds[0],
    onPositionDone,
  });

  const custom = useMemo(() => {
    const allActions = cloneDeep(actions);
    if (entityType === Entities.contract) {
      allActions.push(...positionDoneActions);
    }
    return mergeActions(props.custom, {
      left: allActions,
    });
  }, [props.custom, actions, entityType, positionDoneActions]);

  if (!previewData) {
    return null;
  }

  return (
    <>
      {children({
        ...others,
        entries: entries || [],
        entityType,
        custom,
        previewData,
      })}
    </>
  );
};
